import React, { useState, useEffect } from 'react';
import { Check, AlertCircle, Loader2, Crown, Star, Zap } from 'lucide-react';
import { useAuthenticatedApi } from '../../hooks/useAuthenticatedApi';
import { apiEndpoints } from '../../utils/api';
import { format } from 'date-fns';
import { formatCents } from '../../utils/credits';
import { startCheckout, CheckoutResponse } from '../../lib/paddle';

interface SubscriptionTier {
  id: string;
  name: string;
  display_name: string;
  price: number;
  credits: number;
  description?: string;
  features: string[];
  is_active: boolean;
  metadata?: {
    requires_sales_contact?: boolean;
    signup_enabled?: boolean;
  };
}

interface CurrentSubscription {
  id: string;
  tier_name: string;
  status: string;
  monthly_price: number;
  included_credits: number;
  current_period_end: string;
  next_billing_date: string;
  cancelled_at?: string | null;
  provider?: string;
  migration_required?: boolean;
}

export const SubscriptionSection: React.FC = () => {
  const api = useAuthenticatedApi();
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<CurrentSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [uncancelling, setUncancelling] = useState(false);
  const [subscribing, setSubscribing] = useState<string | null>(null);
  const [planChangeTier, setPlanChangeTier] = useState<SubscriptionTier | null>(null);

  useEffect(() => {
    if (api) {
      fetchSubscriptionData();
    }
  }, [api]);

  const fetchSubscriptionData = async () => {
    try {
      const [tiersResponse, subscriptionResponse] = await Promise.all([
        api.get(apiEndpoints.billing.subscriptionTiers),
        api.get(apiEndpoints.billing.subscription)
      ]);

      setTiers(tiersResponse || []);
      setCurrentSubscription(subscriptionResponse);
    } catch (err) {
      setError('Failed to load subscription data');
    } finally {
      setLoading(false);
    }
  };

  // In-place Paddle plan change: the saved card is charged a prorated amount
  // immediately, with no checkout overlay to review — so it needs a confirm dialog.
  const isInPlacePlanChange = (tier: SubscriptionTier): boolean =>
    !!currentSubscription &&
    currentSubscription.provider === 'paddle' &&
    !currentSubscription.cancelled_at &&
    currentSubscription.tier_name !== tier.name;

  // Webhook fulfilment is async: after a plan change, poll until the mirror reflects
  // the new tier (or give up quietly — the next refresh will catch it).
  const pollForTierChange = async (tierName: string) => {
    for (let attempt = 0; attempt < 8; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 1500));
      try {
        const subscription = await api.get(apiEndpoints.billing.subscription);
        if (subscription?.tier_name === tierName) {
          setCurrentSubscription(subscription);
          return;
        }
      } catch {
        // Keep polling.
      }
    }
    await fetchSubscriptionData();
  };

  const handleSubscribe = async (tierName: string) => {
    setSubscribing(tierName);
    setError(null);
    try {
      const response = await api.post<CheckoutResponse & { plan_change?: boolean; payment_required?: boolean }>(
        apiEndpoints.billing.subscription,
        { tier_name: tierName },
      );

      if (response.plan_change) {
        await pollForTierChange(tierName);
      } else if (response.payment_required) {
        await startCheckout(api, response, { onComplete: fetchSubscriptionData });
      } else {
        await fetchSubscriptionData();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create subscription');
    } finally {
      setSubscribing(null);
    }
  };

  const handleTierClick = (tier: SubscriptionTier) => {
    if (isInPlacePlanChange(tier)) {
      setPlanChangeTier(tier);
    } else {
      handleSubscribe(tier.name);
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await api.delete(apiEndpoints.billing.subscription);
      await fetchSubscriptionData();
      setCancelDialogOpen(false);
    } catch (err: any) {
      setError(err.message || 'Failed to cancel subscription');
    } finally {
      setCancelling(false);
    }
  };

  const handleUncancel = async () => {
    setUncancelling(true);
    try {
      await api.post(apiEndpoints.billing.subscriptionUncancel);
      await fetchSubscriptionData();
    } catch (err: any) {
      setError(err.message || 'Failed to uncancel subscription');
    } finally {
      setUncancelling(false);
    }
  };

  const getTierIcon = (tierName: string) => {
    switch (tierName) {
      case 'creator':
        return { icon: Star, color: 'text-blue-500', bgColor: 'bg-blue-50 dark:bg-blue-900/20' };
      case 'pro':
        return { icon: Zap, color: 'text-brand-primary', bgColor: 'bg-brand-primary/5 dark:bg-brand-primary/10' };
      case 'enterprise':
        return { icon: Crown, color: 'text-purple-500', bgColor: 'bg-purple-50 dark:bg-purple-900/20' };
      default:
        return { icon: Star, color: 'text-gray-500', bgColor: 'bg-gray-50 dark:bg-gray-900/20' };
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 text-brand-primary animate-spin" />
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}
      
      {currentSubscription && currentSubscription.provider === 'citypay' && currentSubscription.migration_required && (
        <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-200">
                We're upgrading our payment system
              </p>
              <p className="text-sm text-amber-800 dark:text-amber-200 mt-1">
                Your current plan ends on{' '}
                <strong>{format(new Date(currentSubscription.current_period_end), 'MMMM d, yyyy')}</strong> and will
                not renew automatically. Please re-subscribe below before then to keep uninterrupted access — your
                remaining credits carry over.
              </p>
            </div>
          </div>
        </div>
      )}

      {currentSubscription && (
        <div className="mb-8 p-6 bg-brand-primary/5 border border-brand-primary/20 rounded-xl">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Current Subscription</h3>
            <div className="flex items-center gap-2">
              <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                currentSubscription.status === 'active' 
                  ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                  : 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
              }`}>
                {currentSubscription.status.toUpperCase()}
              </span>
              {currentSubscription.cancelled_at && (
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400">
                  CANCELLED
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="mb-4">
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">Plan</p>
                <p className="text-xl font-semibold text-neutral-900 dark:text-white">
                  {tiers.find(t => t.name === currentSubscription.tier_name)?.display_name || currentSubscription.tier_name}
                </p>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">Monthly Price</p>
                <p className="text-xl font-semibold text-neutral-900 dark:text-white">
                  ${currentSubscription.monthly_price}/month
                </p>
              </div>
              
              <div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">Included Credits</p>
                <p className="text-xl font-semibold text-neutral-900 dark:text-white">
                  {formatCents(currentSubscription.included_credits)}
                </p>
              </div>
            </div>
            
            <div>
              <div className="mb-4">
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">Current Period Ends</p>
                <p className="text-xl font-semibold text-neutral-900 dark:text-white">
                  {format(new Date(currentSubscription.current_period_end), 'MMMM d, yyyy')}
                </p>
              </div>
              
              <div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                  {currentSubscription.cancelled_at ? 'Subscription Ends' : 'Next Billing Date'}
                </p>
                <p className="text-xl font-semibold text-neutral-900 dark:text-white">
                  {format(new Date(currentSubscription.cancelled_at ? currentSubscription.current_period_end : currentSubscription.next_billing_date), 'MMMM d, yyyy')}
                </p>
              </div>
            </div>
          </div>
          
          {currentSubscription.cancelled_at && (
            <div className="mt-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
              <p className="text-sm text-yellow-800 dark:text-yellow-200">
                Your subscription has been cancelled and will end on{' '}
                <strong>{format(new Date(currentSubscription.current_period_end), 'MMMM d, yyyy')}</strong>.
                You will continue to have access until this date.
              </p>
            </div>
          )}

          <div className="mt-6 flex gap-3">
            {!currentSubscription.cancelled_at && (
              <button
                onClick={() => setCancelDialogOpen(true)}
                className="px-4 py-2 text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                Cancel Subscription
              </button>
            )}
            {currentSubscription.cancelled_at && (
              <button
                onClick={handleUncancel}
                disabled={uncancelling}
                className="px-6 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors disabled:opacity-50"
              >
                {uncancelling ? 'Processing...' : 'Reactivate Subscription'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Always show subscription plans */}
      <div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {tiers.map((tier) => {
            const tierConfig = getTierIcon(tier.name);
            const Icon = tierConfig.icon;
            const isCurrentPlan = currentSubscription && currentSubscription.tier_name === tier.name;
            
            return (
              <div
                key={tier.id}
                className={`relative p-6 rounded-xl border transition-all duration-200 flex flex-col h-full ${
                  !tier.is_active
                    ? 'border-slate-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800 opacity-75'
                    : isCurrentPlan
                    ? 'border-brand-primary bg-brand-primary/5 ring-2 ring-brand-primary/20'
                    : tier.name === 'pro'
                    ? 'border-brand-primary/50 hover:border-brand-primary'
                    : 'border-slate-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 hover:border-brand-primary/50'
                }`}
              >
                {tier.name === 'pro' && !isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-brand-primary text-black px-3 py-1 text-xs font-medium rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-500 text-white px-3 py-1 text-xs font-medium rounded-full">
                      Current Plan
                    </span>
                  </div>
                )}
                
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-lg ${tierConfig.bgColor} flex items-center justify-center`}>
                    <Icon size={20} className={tierConfig.color} />
                  </div>
                  <h4 className="font-bold text-neutral-900 dark:text-white">{tier.display_name}</h4>
                </div>

                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    {tier.name === 'enterprise' || tier.price === 0 ? (
                      <>
                        <span className="text-3xl font-bold text-neutral-900 dark:text-white">$Custom</span>
                        <span className="text-neutral-600 dark:text-neutral-400 text-sm">/contact</span>
                      </>
                    ) : (
                      <>
                        <span className="text-3xl font-bold text-neutral-900 dark:text-white">${tier.price}</span>
                        <span className="text-neutral-600 dark:text-neutral-400 text-sm">/month</span>
                      </>
                    )}
                  </div>
                  {tier.description && (
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mt-2">{tier.description}</p>
                  )}
                </div>

                <ul className="space-y-2 mb-6 flex-grow">
                  {tier.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm">
                      <Check size={16} className="text-green-500 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-neutral-600 dark:text-neutral-400">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto">
                  <button
                    onClick={() => {
                      if (!isCurrentPlan && !tier.metadata?.requires_sales_contact && tier.is_active) {
                        handleTierClick(tier);
                      }
                    }}
                    disabled={tier.metadata?.requires_sales_contact || !!isCurrentPlan || !tier.is_active || !!subscribing}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${
                      isCurrentPlan
                        ? 'bg-neutral-100 dark:bg-neutral-600 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
                        : !tier.is_active
                        ? 'bg-neutral-100 dark:bg-neutral-600 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
                        : tier.metadata?.requires_sales_contact
                        ? 'bg-neutral-100 dark:bg-neutral-600 text-neutral-500 dark:text-neutral-400 cursor-not-allowed'
                        : tier.name === 'pro'
                        ? 'bg-brand-primary text-black hover:bg-brand-primaryDark'
                        : 'bg-neutral-100 dark:bg-neutral-600 text-neutral-700 dark:text-neutral-300 border border-slate-300 dark:border-neutral-500 hover:bg-neutral-200 dark:hover:bg-neutral-500'
                    }`}
                  >
                    {subscribing === tier.name
                      ? 'Processing...'
                      : isCurrentPlan
                      ? 'Current Plan'
                      : !tier.is_active
                      ? 'Coming Soon'
                      : tier.metadata?.requires_sales_contact
                      ? 'Contact Sales'
                      : 'Subscribe'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Plan Change Confirmation Dialog (in-place Paddle proration charges the saved card immediately) */}
      {planChangeTier && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Change Plan
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              Switch to <strong>{planChangeTier.display_name}</strong> (${planChangeTier.price}/month)? A prorated
              amount for the remainder of this billing period will be charged to your saved payment method
              immediately.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setPlanChangeTier(null)}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
              >
                Keep Current Plan
              </button>
              <button
                onClick={() => {
                  const tierName = planChangeTier.name;
                  setPlanChangeTier(null);
                  handleSubscribe(tierName);
                }}
                disabled={!!subscribing}
                className="px-4 py-2 bg-brand-primary text-black font-medium rounded-lg hover:bg-brand-primaryDark transition-colors disabled:opacity-50"
              >
                Confirm Change
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Dialog */}
      {cancelDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-neutral-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white mb-4">
              Cancel Subscription
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 mb-6">
              Are you sure you want to cancel your subscription? You will continue to have access until the end of your current billing period.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setCancelDialogOpen(false)}
                className="px-4 py-2 text-neutral-700 dark:text-neutral-300 border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
              >
                Keep Subscription
              </button>
              <button
                onClick={handleCancel}
                disabled={cancelling}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 